import React from "react";
import cx from 'classnames';

import { SPREADSHEET_TABLE_CONSTANTS, SpreadsheetTableColumn, SpreadsheetTableColumnId } from "../../table.types";
import { Cell } from "./components/cell";
import { Flex } from "@common/components/Flex";

import './header.styles.scss';
import { saveSSColWidth } from "@common/actions/table";

/**
 * Props for the Header component
 */
export type HeaderProps = {
  className?: string;
  columns: Array<SpreadsheetTableColumn>; // Array of column definitions
  columnsWidth: Record<SpreadsheetTableColumnId, number>; // Current width of each column
  onColumnsResize: (columnId: SpreadsheetTableColumnId, newWidth: number, spreadsheetType: string) => void; // Callback for column resizing
  onFilter?: (columnId: number, columnName: string) => void; // Callback to open filter modal
  currentFilters?: Record<string, any>; // Currently applied filters for each column
  onClearColumnFilter?: (columnId: number) => void; // Callback to clear column filter
  spreadsheetType: string; // Type of spreadsheet (for API calls)
};

/**
 * Header component for the spreadsheet table
 * Handles column headers, resizing, filtering, and context menu popup
 */
export const Header: React.FC<HeaderProps> = (props) => {
  const {
    className,
    columns,
    columnsWidth,
    spreadsheetType,
    onColumnsResize,
    onFilter,
    currentFilters = {},
    onClearColumnFilter
  } = props;

  /**
   * Context menu popup state
   * Controls the visibility and position of the right-click context menu
   */
  const [popup, setPopup] = React.useState<{
    show: boolean;
    x: number;
    y: number;
    columnId: number | null;
  }>({
    show: false,
    x: 0,
    y: 0,
    columnId: null,
  });

  /**
   * Handle column resize completion
   * Calls the parent's resize handler with the new column width
   */
  const handleColumnResizeEnd = React.useCallback(async (columnId: SpreadsheetTableColumnId, newColumnWidth: number) => {
    if (spreadsheetType) {
      await onColumnsResize(columnId, newColumnWidth, spreadsheetType);
    }
  }, [onColumnsResize, columnsWidth, columns, spreadsheetType]);

  /**
   * Handle right-click context menu
   * Shows the popup menu at the clicked position for the specific column
   */
  const handleContextMenu = React.useCallback((e: React.MouseEvent<HTMLTableCellElement>, columnId: SpreadsheetTableColumnId) => {
    e.preventDefault();
    e.stopPropagation();

    setPopup({
      show: true,
      x: e.clientX,
      y: e.clientY,
      columnId,
    });
  }, []);

  /**
   * Handle clear column filter action
   * Removes the filter for the selected column and closes the popup
   */
  const handleClearColumnFilterClick = React.useCallback(() => {
    if (popup.columnId && onClearColumnFilter) {
      onClearColumnFilter(popup.columnId);
    }
    setPopup({ show: false, x: 0, y: 0, columnId: null });
  }, [popup.columnId, onClearColumnFilter]);

  /**
   * Handle filter action from context menu
   * Opens the filter modal for the selected column
   */
  const handleFilterClick = React.useCallback(() => {
    if (popup.columnId && onFilter) {
      const column = columns.find(col => col.id === popup.columnId);
      const columnName = column?.colName ? String(column.colName) : String(popup.columnId);

      // This calls the parent's handleFilterOpen function
      onFilter(popup.columnId, columnName); // ← Triggers parent to open Filters modal
    }
    setPopup({ show: false, x: 0, y: 0, columnId: null });
  }, [popup.columnId, onFilter, columns]);

  /**
   * Close popup when clicking outside
   * Adds/removes click event listener to handle clicking outside the popup
   */
  React.useEffect(() => {
    const handleClickOutside = () => {
      setPopup({ show: false, x: 0, y: 0, columnId: null });
    };

    if (popup.show) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [popup.show]);

  // Calculate total width of all columns for the Flex container
  const totalWidth = columns.reduce((acc, column) => acc + (columnsWidth[column.id] || 150), 0);

  // Check if the current column in popup has an active filter
  const currentColumnHasFilter = popup.columnId ? !!currentFilters[popup.columnId] : false;

  return (
      <>
        {/* Main header container with all column headers */}
        <Flex width={totalWidth} className={cx("SpreadsheetDetailsSpreadsheetResultTable__header", className)}>
          {columns.map((column, index) => {
            // Special handling for row count column (first column)
            const isRowCount = column.id === SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID;
            const uniqueKey = `${column.id}-${index}`;
            return (
                <Cell
                    onColumnResizeEnd={handleColumnResizeEnd}
                    currentColumnWidth={columnsWidth[column.id] ?? (isRowCount ? 50 : 150)}
                    loading={false}
                    key={uniqueKey}
                    column={column}
                    onContextMenu={handleContextMenu}
                    className={isRowCount ? 'SpreadsheetDetailsSpreadsheetResultTable__first-column' : undefined}
                    isFiltered={!!currentFilters[column.id]}
                />
            );
          })}
        </Flex>

        {/* Context menu popup for column actions */}
        {popup.show && (
            <div
                className="SpreadsheetTablePopup"
                style={{
                  position: 'fixed',
                  left: popup.x,
                  top: popup.y,
                  zIndex: 1050,
                  background: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  padding: '4px 0',
                  minWidth: '120px',
                }}
                onClick={(e) => e.stopPropagation()}
            >
              {/* Filter action button */}
              <button
                  onClick={handleFilterClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#333',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
              >
                Filter
              </button>

              {/* Show Clear Filter option if this column has an active filter */}
              {currentColumnHasFilter && (
                  <button
                      onClick={handleClearColumnFilterClick}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        width: '100%',
                        padding: '8px 12px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#333',
                        textAlign: 'left',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                  >
                    Clear Filter
                  </button>
              )}
            </div>
        )}
      </>
  );
};